import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Plus, Minus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { track, useLang } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";
import { Reveal } from "@/components/Reveal";
import { PageHead } from "@/components/Shared";
import { usePageMeta } from "@/lib/seo";
import { BSPORT } from "@/data/config";

// Language-neutral membership data (prices, per-class values, Bsport links).
const MEMBERSHIPS = [
  {
    key: "classic", name: "Classic", freq: "1× per week", creditsCount: 4,
    six: "€109", twelve: "€99", perSix: 27, perTwelve: 25,
    sixUrl: "https://backoffice.bsport.io/checkout/6040/subscription/51688?force=true",
    twelveUrl: "https://backoffice.bsport.io/checkout/6040/subscription/53960?force=true",
  },
  {
    key: "signature", name: "Signature", freq: "2× per week", creditsCount: 8, popular: true,
    six: "€189", twelve: "€179", perSix: 24, perTwelve: 22,
    sixUrl: "https://backoffice.bsport.io/checkout/6040/subscription/51689?force=true",
    twelveUrl: "https://backoffice.bsport.io/checkout/6040/subscription/53961?force=true",
  },
  {
    key: "elite", name: "Elite", freq: "3× per week", creditsCount: 12,
    six: "€239", twelve: "€229", perSix: 20, perTwelve: 19,
    sixUrl: "https://backoffice.bsport.io/checkout/6040/subscription/51690?force=true",
    twelveUrl: "https://backoffice.bsport.io/checkout/6040/subscription/53962?force=true",
  },
];

const PACKS = [
  { id: "5", price: "€165", url: BSPORT.PACK_5_URL },
  { id: "10", price: "€310", url: BSPORT.PACK_10_URL },
];

const PRIVATE_PRICES = ["€99", "€140"];

const COPY = {
  en: {
    heroLabel: "Pricing",
    heroTitle: "Find your rhythm.",
    heroBody: "From your first class to a consistent weekly practice, choose the option that fits your rhythm.",
    trust: ["★★★★★ Google reviews", "Max. 9 people per class", "Beginners welcome"],
    startLabel: "Your first experience",
    startTitle: "Introductory offer",
    startPrice: "3 classes · €59",
    startBullets: ["3 Reformer Pilates classes", "Valid for 21 days", "Available once per new client"],
    startSettle: "Intro: 3 classes for €59. Choose a membership within 7 days of your last intro class and we'll credit the €59 towards your first month.",
    startCta: "Start with 3 classes",
    memLabel: "Memberships",
    memTitle: "Make it your ritual.",
    memBody: "Choose how often you want to train and the commitment that suits you.",
    memBilling: "Billed every 4 weeks · Choose a 6-period or 12-period commitment",
    toggle6: "6 periods",
    toggle12: "12 periods",
    toggleNote12: "Best value · Save €10 / 4 weeks",
    toggleNote6: "12 periods saves €10 / 4 weeks",
    commit6: "6-period commitment",
    commit12: "12-period commitment",
    per4: "/ 4 weeks",
    choose: "Choose →",
    mostPopular: "Most popular",
    usp: "Max. 9 people per class",
    save: "Save €10 / 4 weeks",
    creditsTpl: (n) => `${n} credits every 4 weeks`,
    perClassTpl: (v) => `≈ €${v} per class`,
    memNote: "Your credits can be used for any Maison Core group class. Unused credits remain available for an additional 4 weeks.",
    packsLabel: "Class packs",
    packsTitle: "Move on your terms.",
    packsBody: "Prefer flexibility without a recurring membership? Choose a class pack and book whenever it suits you.",
    packs: [
      { name: "5 class pack", per: "€33 per class", cta: "Choose 5 classes" },
      { name: "10 class pack", per: "€31 per class", cta: "Choose 10 classes" },
    ],
    singleLabel: "Single class",
    singleBody: "One Maison Core class. 45 minutes of strength, control and precision.",
    singleCta: "Choose single class →",
    privateLabel: "Private practice",
    privateBody: "For a more personal approach — one-to-one or shared Reformer sessions tailored to your goals, experience and movement needs, with focused guidance and dedicated attention.",
    privatePacksNote: "Private packs — 5 or 10 sessions at a lower price per session, from €90 / session.",
    privatePacksCta: "View private packs →",
    privateNames: ["1:1 Private", "Duo"],
    privateCta: "Discover Private Pilates →",
    giftLabel: "Looking for a gift?",
    giftBody: "Give the gift of Maison Core — from a first Reformer class to a private Pilates experience.",
    giftCta: "Explore Gift Cards →",
    helpTitle: "Not sure which option fits you?",
    help: [
      { t: "New to Maison Core", b: "Start with the 3-class introductory offer.", cta: "Start your intro →", intro: true },
      { t: "Training weekly", b: "A membership gives you a consistent rhythm and the best value.", cta: "View memberships →", anchor: "memberships" },
      { t: "Need flexibility", b: "Choose a class pack.", cta: "View class packs →", anchor: "packs" },
    ],
  },
  nl: {
    heroLabel: "Tarieven",
    heroTitle: "Vind je ritme.",
    heroBody: "Van je eerste les tot een vaste wekelijkse routine — kies de optie die bij jouw ritme past.",
    trust: ["★★★★★ Google-reviews", "Max. 9 personen per les", "Beginners welkom"],
    startLabel: "Jouw eerste ervaring",
    startTitle: "Introductieaanbod",
    startPrice: "3 lessen · €59",
    startBullets: ["3 Reformer Pilates lessen", "21 dagen geldig", "Eenmalig per nieuwe klant"],
    startSettle: "Intro: 3 lessen voor €59. Kies je binnen 7 dagen na je laatste introles voor een membership, dan verrekenen we de €59 met je eerste maand.",
    startCta: "Start met 3 lessen",
    memLabel: "Abonnementen",
    memTitle: "Maak er jouw ritueel van.",
    memBody: "Kies hoe vaak je wilt trainen en de looptijd die bij je past.",
    memBilling: "Elke 4 weken gefactureerd · Kies een looptijd van 6 of 12 periodes",
    toggle6: "6 periodes",
    toggle12: "12 periodes",
    toggleNote12: "Beste waarde · Bespaar €10 / 4 weken",
    toggleNote6: "12 periodes bespaart €10 / 4 weken",
    commit6: "Looptijd 6 periodes",
    commit12: "Looptijd 12 periodes",
    per4: "/ 4 weken",
    choose: "Kies →",
    mostPopular: "Meest gekozen",
    usp: "Max. 9 deelnemers per les",
    save: "Bespaar €10 / 4 weken",
    creditsTpl: (n) => `${n} credits per 4 weken`,
    perClassTpl: (v) => `≈ €${v} per les`,
    memNote: "Je credits zijn te gebruiken voor elke Maison Core groepsles. Ongebruikte credits blijven nog 4 weken beschikbaar.",
    packsLabel: "Lessenkaarten",
    packsTitle: "Beweeg op jouw voorwaarden.",
    packsBody: "Liever flexibiliteit zonder doorlopend abonnement? Kies een lessenkaart en boek wanneer het jou uitkomt.",
    packs: [
      { name: "5-lessenkaart", per: "€33 per les", cta: "Kies 5 lessen" },
      { name: "10-lessenkaart", per: "€31 per les", cta: "Kies 10 lessen" },
    ],
    singleLabel: "Losse les",
    singleBody: "Eén Maison Core les. 45 minuten kracht, controle en precisie.",
    singleCta: "Kies een losse les →",
    privateLabel: "Privéles",
    privateBody: "Voor een persoonlijkere aanpak — privé Reformer-sessies (1:1 of samen) afgestemd op jouw doelen, ervaring en bewegingsbehoeften, met gerichte begeleiding en volledige aandacht.",
    privatePacksNote: "Privépakketten — 5 of 10 sessies met een lagere prijs per sessie, vanaf €90 / sessie.",
    privatePacksCta: "Bekijk privépakketten →",
    privateNames: ["1:1 Privé", "Duo"],
    privateCta: "Ontdek Private Pilates →",
    helpTitle: "Niet zeker welke optie bij je past?",
    giftLabel: "Op zoek naar een cadeau?",
    giftBody: "Geef Maison Core cadeau — van een eerste Reformer-les tot een privé Pilates-ervaring.",
    giftCta: "Bekijk cadeaubonnen →",
    help: [
      { t: "Nieuw bij Maison Core", b: "Begin met het introductieaanbod van 3 lessen.", cta: "Start je intro →", intro: true },
      { t: "Wekelijks trainen", b: "Een abonnement geeft je een vast ritme en de beste waarde.", cta: "Bekijk abonnementen →", anchor: "memberships" },
      { t: "Flexibiliteit nodig", b: "Kies een lessenkaart.", cta: "Bekijk lessenkaarten →", anchor: "packs" },
    ],
  },
};

function DetailItem({ item, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-onyx/15" data-testid={`pricing-detail-${item.n}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-6 py-6 text-left" aria-expanded={open}>
        <span className="flex items-baseline gap-5">
          <span className="label text-terracotta">{item.n}</span>
          <span className="font-serif text-2xl leading-snug md:text-3xl">{item.t}</span>
        </span>
        <span className="shrink-0 text-terracotta">{open ? <Minus strokeWidth={1.3} /> : <Plus strokeWidth={1.3} />}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
            <p className="max-w-2xl pb-8 font-sans text-base leading-relaxed text-onyx/70">{item.b}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Pricing() {
  const { lang, t } = useLang();
  const c = COPY[lang] || COPY.en;
  const [period, setPeriod] = useState("twelve");
  const isTwelve = period === "twelve";

  usePageMeta(
    lang === "nl"
      ? {
          title: "Tarieven — Maison Core Reformer Pilates, Amsterdam",
          description: "Abonnementen, lessenkaarten en privé Reformer Pilates bij Maison Core Amsterdam. Vanaf €99 / 4 weken. Start met 3 lessen voor €59.",
          canonical: "https://maisoncore.nl/pricing",
        }
      : {
          title: "Pricing — Maison Core Reformer Pilates, Amsterdam",
          description: "Memberships, class packs and private Reformer Pilates at Maison Core Amsterdam. From €99 / 4 weeks. Start with 3 classes for €59.",
          canonical: "https://maisoncore.nl/pricing",
        }
  );

  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#private") return;
    const t = setTimeout(() => {
      const el = document.getElementById("private");
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 450);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <PageHead label={c.heroLabel} title={c.heroTitle} lead={c.heroBody} />

      {/* TRUST ROW */}
      <section className="bg-petal" data-testid="pricing-trust">
        <div className="mx-auto max-w-[1600px] px-6 pb-12 md:px-12">
          <div className="flex flex-wrap gap-x-10 gap-y-3 border-t border-onyx/10 pt-6">
            {c.trust.map((x) => (
              <span key={x} className="label text-onyx/55" data-testid="pricing-trust-item">{x}</span>
            ))}
          </div>
        </div>
      </section>

      {/* START HERE */}
      <section className="bg-rust text-petal" data-testid="pricing-intro">
        <div className="mx-auto grid max-w-[1600px] gap-10 px-6 py-16 md:grid-cols-12 md:px-12 md:py-20">
          <div className="md:col-span-7">
            <SectionLabel light>{c.startLabel}</SectionLabel>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl">{c.startTitle}</h2>
            <p className="mt-6 font-serif text-4xl italic text-blush">{c.startPrice}</p>
            <ul className="mt-6 space-y-1 font-sans text-sm text-petal/80">
              {c.startBullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <p className="mt-6 max-w-lg font-sans text-sm leading-relaxed text-petal/60" data-testid="pricing-intro-settle">{c.startSettle}</p>
          </div>
          <div className="flex items-end md:col-span-4 md:col-start-9">
            <a href={BSPORT.INTRO_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("intro_offer_click", { source: "pricing" })} className="label w-full border border-petal bg-petal py-5 text-center text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal" data-testid="pricing-intro-cta">{c.startCta}</a>
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS */}
      <section id="memberships" className="scroll-mt-24 bg-petal" data-testid="pricing-memberships">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.memLabel}</SectionLabel>
          <h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-7xl">{c.memTitle}</h2>
          <p className="mt-6 max-w-xl font-sans text-lg text-onyx/70">{c.memBody}</p>
          <p className="label mt-4 text-terracotta">{c.memBilling}</p>

          {/* PERIOD TOGGLE */}
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="inline-flex w-full items-center border border-onyx/25 p-1 sm:w-auto" data-testid="pricing-period-toggle">
              {[{ id: "six", label: c.toggle6 }, { id: "twelve", label: c.toggle12 }].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { setPeriod(opt.id); track("period_toggle", { period: opt.id, source: "pricing" }); }}
                  aria-pressed={period === opt.id}
                  className={`label flex-1 whitespace-nowrap px-5 py-3 text-center transition-colors duration-300 sm:flex-none sm:px-8 ${period === opt.id ? "bg-onyx text-petal" : "text-onyx/55 hover:text-onyx"}`}
                  data-testid={`pricing-period-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="label text-terracotta">{isTwelve ? c.toggleNote12 : c.toggleNote6}</span>
          </div>

          <div className="mt-10 space-y-4">
            {MEMBERSHIPS.map((m) => (
              <Reveal key={m.key}>
                <div className={`grid grid-cols-1 gap-5 border p-6 md:grid-cols-12 md:items-center md:gap-6 md:p-8 ${m.popular ? "border-onyx bg-blush/25" : "border-onyx/20"}`} data-testid={`pricing-member-${m.key}`}>
                  <div className="md:col-span-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-serif text-4xl md:text-5xl">{m.name}</span>
                      {m.popular && <span className="label text-terracotta">{c.mostPopular}</span>}
                    </div>
                    <p className="mt-2 font-sans text-base text-onyx/70">{m.freq}</p>
                    <p className="label mt-1 text-clay">{c.creditsTpl(m.creditsCount)}</p>
                    <p className="mt-3 flex items-center gap-2 font-sans text-xs text-onyx/60" data-testid={`pricing-member-usp-${m.key}`}><Users size={14} strokeWidth={1.4} className="text-terracotta" />{c.usp}</p>
                  </div>

                  <div className="border-t border-onyx/10 pt-5 md:col-span-4 md:border-0 md:pt-0">
                    <span className="label text-onyx/50">{isTwelve ? c.commit12 : c.commit6}</span>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.p
                        key={period}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-1 font-serif text-4xl md:text-5xl"
                      >
                        {isTwelve ? m.twelve : m.six}<span className="ml-1 font-sans text-xs opacity-60">{c.per4}</span>
                      </motion.p>
                    </AnimatePresence>
                    {isTwelve && <p className="mt-1 font-sans text-xs italic text-terracotta">{c.save}</p>}
                    <p className="mt-1 font-sans text-xs text-clay" data-testid={`pricing-member-perclass-${m.key}`}>{c.perClassTpl(isTwelve ? m.perTwelve : m.perSix)}</p>
                  </div>

                  <div className="md:col-span-3">
                    <a
                      href={isTwelve ? m.twelveUrl : m.sixUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("membership_click", { plan: m.key, term: isTwelve ? "12-period" : "6-period", source: "pricing" })}
                      className="label flex w-full items-center justify-center border border-onyx py-4 text-onyx transition-colors duration-500 hover:bg-onyx hover:text-petal"
                      data-testid={`pricing-member-cta-${m.key}`}
                    >
                      {c.choose}
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 font-sans text-sm text-onyx/55">{c.memNote}</p>
        </div>
      </section>

      {/* CLASS PACKS */}
      <section id="packs" className="scroll-mt-24 bg-onyx text-petal" data-testid="pricing-packs">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
          <SectionLabel light>{c.packsLabel}</SectionLabel>
          <h2 className="mt-8 font-serif text-5xl md:text-6xl">{c.packsTitle}</h2>
          <p className="mt-6 max-w-xl font-sans text-lg text-petal/75">{c.packsBody}</p>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {PACKS.map((p, i) => (
              <div key={p.id} className="flex flex-col justify-between gap-8 border border-petal/20 p-8" data-testid={`pricing-pack-${p.id}`}>
                <div>
                  <h3 className="font-serif text-4xl">{c.packs[i].name}</h3>
                  <p className="mt-3 font-serif text-3xl">{p.price}<span className="ml-2 font-sans text-sm text-blush">{c.packs[i].per}</span></p>
                </div>
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="label border border-petal py-4 text-center transition-colors duration-500 hover:bg-petal hover:text-onyx" data-testid={`pricing-pack-cta-${p.id}`}>{c.packs[i].cta}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SINGLE + PRIVATE */}
      <section className="bg-petal" data-testid="pricing-single-private">
        <div className="mx-auto grid max-w-[1600px] gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-24">
          <div className="border-t border-onyx/15 pt-8">
            <SectionLabel>{c.singleLabel}</SectionLabel>
            <p className="mt-6 font-serif text-5xl">€35</p>
            <p className="mt-3 max-w-sm font-sans text-sm text-onyx/65">{c.singleBody}</p>
            <a href={BSPORT.SINGLE_CLASS_URL} target="_blank" rel="noopener noreferrer" className="label mt-6 inline-block border-b border-onyx pb-1 hover:opacity-60" data-testid="pricing-single-cta">{c.singleCta}</a>
          </div>
          <div id="private" className="scroll-mt-28 border-t border-onyx/15 pt-8">
            <SectionLabel>{c.privateLabel}</SectionLabel>
            <p className="mt-6 max-w-sm font-sans text-sm text-onyx/65">{c.privateBody}</p>
            <div className="mt-6 space-y-2 font-serif text-3xl">
              {c.privateNames.map((n, i) => (
                <p key={n}>{n} <span className="text-onyx/60">{PRIVATE_PRICES[i]}</span></p>
              ))}
            </div>
            <p className="mt-4 max-w-sm font-sans text-xs text-onyx/55">{c.privatePacksNote}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <Link to="/private" onClick={() => track("private_page_click", { source: "pricing" })} className="label inline-block border-b border-onyx pb-1 hover:opacity-60" data-testid="pricing-private-cta">{c.privateCta}</Link>
              <Link to="/private" onClick={() => track("private_packs_click", { source: "pricing" })} className="label inline-block border-b border-onyx/40 pb-1 text-onyx/60 hover:opacity-60" data-testid="pricing-private-packs-cta">{c.privatePacksCta}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* GIFT CARDS */}
      <section className="bg-petal" data-testid="pricing-gift">
        <div className="mx-auto max-w-[1600px] px-6 pb-4 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 border-t border-onyx/15 py-12 md:flex-row md:items-center">
            <div>
              <SectionLabel>{c.giftLabel}</SectionLabel>
              <p className="mt-5 max-w-xl font-serif text-2xl leading-snug text-onyx md:text-3xl">{c.giftBody}</p>
            </div>
            <Link to="/gift-cards" onClick={() => track("gift_cards_click", { source: "pricing" })} className="label shrink-0 border-b border-onyx pb-1 hover:opacity-60" data-testid="pricing-gift-cta">{c.giftCta}</Link>
          </div>
        </div>
      </section>

      {/* HELP ME CHOOSE */}
      <section className="bg-blush/40" data-testid="pricing-help">
        <div className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-20">
          <h2 className="font-serif text-4xl md:text-5xl">{c.helpTitle}</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {c.help.map((h, i) =>
              h.intro ? (
                <a
                  key={h.t}
                  href={BSPORT.INTRO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("intro_offer_click", { source: "pricing-help" })}
                  className="group border-t border-onyx/20 pt-5 text-left transition-opacity hover:opacity-80"
                  data-testid={`pricing-help-${i}`}
                >
                  <p className="label text-terracotta">{h.t}</p>
                  <p className="mt-3 font-serif text-2xl leading-snug">{h.b}</p>
                  <span className="label mt-5 inline-block border-b border-onyx pb-1 transition-opacity group-hover:opacity-60">{h.cta}</span>
                </a>
              ) : (
                <button
                  key={h.t}
                  type="button"
                  onClick={() => document.getElementById(h.anchor)?.scrollIntoView({ behavior: "smooth" })}
                  className="group border-t border-onyx/20 pt-5 text-left transition-opacity hover:opacity-80"
                  data-testid={`pricing-help-${i}`}
                >
                  <p className="label text-terracotta">{h.t}</p>
                  <p className="mt-3 font-serif text-2xl leading-snug">{h.b}</p>
                  <span className="label mt-5 inline-block border-b border-onyx pb-1 transition-opacity group-hover:opacity-60">{h.cta}</span>
                </button>
              )
            )}
          </div>
        </div>
      </section>
      {/* MEMBERSHIP DETAILS */}
      <section className="bg-petal" data-testid="pricing-membership-details">
        <div className="mx-auto max-w-[1600px] px-6 pb-20 md:px-12 md:pb-28">
          <div className="grid gap-10 border-t border-onyx/15 pt-16 md:grid-cols-12">
            <div className="md:col-span-4">
              <SectionLabel>{t.welcome.detailsLabel}</SectionLabel>
              <h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-6xl">{t.welcome.detailsTitle}</h2>
              <p className="label mt-8 text-terracotta">{t.welcome.detailsQuick}</p>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              {t.welcome.details.map((d, i) => (
                <DetailItem key={d.n} item={d} defaultOpen={i === 0} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
